// Erreurs de l'API (socle §4.2, ADR 0006) : `{ message, code, details | errors }`, messages en français affichables tels quels.
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';

export interface ApiErrorBody {
  message?: string;
  code?: string;
  details?: Record<string, unknown>;
  errors?: Record<string, string[]>;
}

export type ApiErrorKind = 'network' | 'unauthenticated' | 'forbidden' | 'not_found' | 'validation' | 'conflict' | 'gone' | 'rate_limited' | 'server' | 'business';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly fieldErrors: Record<string, string[]>;

  constructor(status: number, message: string, code: string, details: Record<string, unknown> = {}, fieldErrors: Record<string, string[]> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.fieldErrors = fieldErrors;
  }

  static fromResponse(status: number, body?: ApiErrorBody): ApiError {
    return new ApiError(
      status,
      body?.message ?? 'Une erreur inattendue est survenue. Réessayez dans un instant.',
      body?.code ?? (status >= 500 ? 'SERVER_ERROR' : 'HTTP_ERROR'),
      body?.details ?? {},
      body?.errors ?? {},
    );
  }

  static network(cause: unknown): ApiError {
    const error = new ApiError(0, 'Connexion impossible avec le serveur. Vérifiez votre réseau puis réessayez.', 'NETWORK_ERROR');
    error.cause = cause;
    return error;
  }

  get kind(): ApiErrorKind {
    if (this.status === 0) return 'network';
    if (this.status === 401) return 'unauthenticated';
    if (this.status === 403) return 'forbidden';
    if (this.status === 404) return 'not_found';
    if (this.status === 410) return 'gone';
    if (this.status === 429) return 'rate_limited';
    if (this.code === 'VERSION_CONFLICT') return 'conflict';
    if (this.status === 422 && this.code === 'VALIDATION_FAILED') return 'validation';
    if (this.status >= 500) return 'server';
    return 'business';
  }

  /** Référence affichée à l'utilisateur pour les erreurs serveur (ETAT-04 « Code : ERR-500-… »). */
  get reference(): string | undefined {
    const ref = this.details.reference;
    return typeof ref === 'string' ? ref : undefined;
  }

  get serverVersion(): number | undefined {
    const version = this.details.server_version;
    return typeof version === 'number' ? version : undefined;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Relie les erreurs 422 de l'API aux champs du formulaire (`security.session_timeout_minutes` → même chemin).
 * Retourne true si au moins une erreur a été reliée ; sinon l'appelant affiche le message global.
 */
export function applyFieldErrors<T extends FieldValues>(error: unknown, setError: UseFormSetError<T>): boolean {
  if (!isApiError(error) || error.kind !== 'validation') return false;
  const entries = Object.entries(error.fieldErrors);
  entries.forEach(([field, messages], index) => {
    setError(field as Path<T>, { type: 'server', message: messages[0] }, { shouldFocus: index === 0 });
  });
  return entries.length > 0;
}
